/** @jsxImportSource @emotion/react */

import { FC, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { Link, useParams } from 'react-router-dom'
import { PlatformName } from '__generatedTypes__/globalTypes'
import { findOneUser_findOneUser as User } from '__generatedTypes__/findOneUser'
import { findUsersProgress, findUsersProgressVariables } from '__generatedTypes__/findUsersProgress'
import { GET_USERS_CASPER_PROGRESS, GET_USERS_PROGRESS } from 'queries/users'
import { findAllPlatforms, findAllPlatformsVariables } from '__generatedTypes__/findAllPlatforms'
import { findUsersCasperProgress, findUsersCasperProgressVariables } from '__generatedTypes__/findUsersCasperProgress'
import { PlatformSlug, TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { GET_PLATFORMS } from 'queries/common'
import Loader from 'components/Loader'
import Select from 'components/Select'
import { getFullPlatformTitle } from 'components/Layout/Sidebar/PlatformList'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import * as tabsClasses from '../styles'
import * as classes from './styles'

const PLATFORMS_WITHOUT_SUBJECT_PTS = [
  PlatformSlug.INBDEBooster,
  PlatformSlug.NBDHEBooster,
  PlatformSlug.NDEBCrusher,
  PlatformSlug.PATBooster,
]

type Props = {
  user: User
}

export const MembershipUsage: FC<Props> = ({ user }) => {
  const { platform: platformSlug } = useParams()
  const { id: userId } = user
  const [selectedPlatform, setSelectedPlatform] = useState<{ id: string; slug: string; title: string } | null>(null)

  const { getLink } = useLink()

  const { data: platformsData, loading: platformsLoading } = useQuery<findAllPlatforms, findAllPlatformsVariables>(
    GET_PLATFORMS,
    {
      variables: { query: { take: TAKE_ALL_ITEMS_COUNT } },
      onCompleted: (data) => {
        const platforms = data?.findAllPlatforms.items
        const platform = platforms?.find((p) => p.slug === platformSlug) ?? platforms?.[0]

        if (platform) {
          setSelectedPlatform({
            id: platform.id,
            title: platform.title,
            slug: platform.slug,
          })
        }
      },
    }
  )

  const { data, loading: progressLoading } = useQuery<findUsersProgress, findUsersProgressVariables>(
    GET_USERS_PROGRESS,
    {
      variables: {
        platformId: String(selectedPlatform?.id),
        findUsersProgressId: userId,
      },
      skip: !selectedPlatform || selectedPlatform.slug === PlatformSlug.CasperBooster,
    }
  )

  const { data: casperData, loading: casperProgressLoading } = useQuery<
    findUsersCasperProgress,
    findUsersCasperProgressVariables
  >(GET_USERS_CASPER_PROGRESS, {
    variables: {
      id: userId,
      isFirstAttempt: null,
    },
    skip: !selectedPlatform || selectedPlatform.slug !== PlatformSlug.CasperBooster,
  })

  const handleChangePlatform = (value: { id: string; slug: string; title: string } | null) => {
    setSelectedPlatform(value)
  }

  const platforms =
    useMemo(
      () => platformsData?.findAllPlatforms.items.map(({ id, slug, title }) => ({ id, slug, title })),
      [platformsData]
    ) || []

  const userProgress = data?.findUsersProgress
  const casperUserProgress = casperData?.findUsersCasperProgress

  const renderProgress = () => {
    if (progressLoading || casperProgressLoading) {
      return <Loader />
    }

    if (selectedPlatform?.slug !== PlatformSlug.CasperBooster ? !userProgress?.length : !casperUserProgress) {
      return <Typography>No progress found</Typography>
    }

    if (selectedPlatform?.slug === PlatformSlug.CasperBooster) {
      return (
        <ul css={classes.subjectCardsWrapper}>
          <li>
            <div css={[classes.subjectCardLink, classes.withoutScale]}>
              <Typography variant="title4" css={classes.subjectTitle}>
                Casper
              </Typography>
              <Divider />
              <ul css={classes.progressList}>
                <li css={classes.progressListItem}>
                  <div>Question Progress</div>
                  <div data-testid="question-progress-casper">
                    {casperUserProgress?.scenarioQBank?.answeredQuestionsCount ?? 0}/
                    {casperUserProgress?.scenarioQBank?.totalQuestionsCount ?? 0}
                  </div>
                </li>
                <li css={classes.progressListItem}>
                  <div>Video Response Practice</div>
                  <div data-testid="video-progress-casper">
                    {casperUserProgress?.videoResponsePractice?.answeredQuestionsCount ?? 0}/
                    {casperUserProgress?.videoResponsePractice?.totalQuestionsCount ?? 0}
                  </div>
                </li>
                <li css={classes.progressListItem}>
                  <div>Text Response Practice</div>
                  <div data-testid="practice-tests-progress-casper">
                    {casperUserProgress?.textResponsePractice?.answeredQuestionsCount ?? 0}/
                    {casperUserProgress?.textResponsePractice?.totalQuestionsCount ?? 0}
                  </div>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      )
    }

    return (
      <ul css={classes.subjectCardsWrapper}>
        {userProgress?.map(({ subject, questionProgress, videoProgress, practiceTestsProgress }) => (
          <li key={subject.id} data-testid={`subject-item-${subject.id}`}>
            <Link
              to={getLink(cmsRoutes.USER_EDIT_PT_DETAILS, { userId, platformId: String(selectedPlatform?.id) })}
              css={classes.subjectCardLink}
            >
              <Typography variant="title4" css={classes.subjectTitle}>
                {subject.tabName}
              </Typography>
              <Divider />
              <ul css={classes.progressList}>
                <li css={classes.progressListItem}>
                  <div>Question Progress</div>
                  <div data-testid="question-progress">
                    {questionProgress.finishedQuestionsCount}/{questionProgress.totalQuestionsCount}
                  </div>
                </li>
                <li css={classes.progressListItem}>
                  <div>Video Progress</div>
                  <div data-testid="video-progress">
                    {videoProgress.watchedVideosCount}/{videoProgress.totalVideosCount}
                  </div>
                </li>
                {!PLATFORMS_WITHOUT_SUBJECT_PTS.includes(selectedPlatform?.slug as PlatformSlug) && (
                  <li css={classes.progressListItem}>
                    <div>Practice Tests Completed</div>
                    <div data-testid="practice-tests-progress">
                      {practiceTestsProgress.completedPracticeTests}/{practiceTestsProgress.totalPracticeTests}
                    </div>
                  </li>
                )}
              </ul>
            </Link>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <section css={tabsClasses.container}>
      <div css={tabsClasses.form}>
        <Typography variant="title3" css={tabsClasses.title}>
          Membership Usage
        </Typography>
        <Divider />
        <Select
          items={platforms}
          value={selectedPlatform}
          onChange={handleChangePlatform}
          renderItem={(platform) => getFullPlatformTitle(platform.title as PlatformName)}
          getItemLabel={(platform) => getFullPlatformTitle(platform.title as PlatformName) ?? platform.title}
          placeholder="Platform"
          label="Platform"
          loading={platformsLoading}
          hideSelectedItems
        />

        {renderProgress()}
      </div>
    </section>
  )
}
